import React, { useMemo, useState } from "react";
import HistoryIcon from "@mui/icons-material/History";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { auth, db } from "../../Config/firebase";

function converterData(valor) {
  if (!valor) return null;
  if (typeof valor.toDate === "function") return valor.toDate();
  if (valor.seconds) return new Date(valor.seconds * 1000);
  if (valor instanceof Date) return valor;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

function formatarDataHora(valor) {
  const data = converterData(valor);
  if (!data) return "Data e hora não informadas";
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function primeiroValor(objeto, campos) {
  return campos.map((campo) => objeto?.[campo]).find(Boolean);
}

function formatarResponsavel(...valores) {
  for (const valor of valores) {
    if (!valor) continue;
    if (typeof valor === "object") {
      const textoObjeto = valor.email || valor.nome || valor.name || valor.uid || valor.id;
      if (textoObjeto) return String(textoObjeto);
      continue;
    }
    return String(valor);
  }
  return "Usuário não identificado";
}

function normalizarTitulo(item) {
  const mapa = {
    CRIACAO: "Criação do cadastro",
    EDICAO: "Alteração do cadastro",
    ALTERACAO: "Alteração do cadastro",
    ALTERACAO_STATUS: "Alteração de situação",
    CANCELAMENTO: "Cancelamento do cadastro",
    REATIVACAO: "Reativação do cadastro",
  };
  const acao = item.acao || item.tipo || "";
  return mapa[acao] || item.titulo || (acao
    ? String(acao).replaceAll("_", " ").toLocaleLowerCase("pt-BR")
    : "Registro de alteração");
}

function criarEventosBasicos(registro = {}) {
  const eventos = [];
  const criacao = primeiroValor(registro, ["criadoEm", "createdAt", "dataCriacao", "dataCadastro"]);
  const atualizacao = primeiroValor(registro, ["atualizadoEm", "updatedAt", "dataAtualizacao", "ultimaAlteracao"]);

  if (criacao) {
    eventos.push({
      id: "evento-criacao",
      titulo: "Criação do cadastro",
      descricao: "Cadastro criado no sistema.",
      data: criacao,
      usuarioEmail: registro.criadoPorEmail || "",
      usuario: registro.criadoPorNome || registro.criadoPor || registro.usuarioCriacao || "Não informado",
    });
  }
  if (atualizacao && formatarDataHora(atualizacao) !== formatarDataHora(criacao)) {
    eventos.push({
      id: "evento-atualizacao",
      titulo: "Última alteração registrada",
      descricao: "O cadastro possui uma atualização registrada, mas os detalhes dos campos não estavam disponíveis.",
      data: atualizacao,
      usuarioEmail: registro.atualizadoPorEmail || "",
      usuario: registro.atualizadoPorNome || registro.atualizadoPor || registro.usuarioAtualizacao || "Não informado",
    });
  }
  return eventos;
}

export default function HistoricoCadastro({ colecao, documentoId, registro, iconeApenas = true, resolverEmailPorUid = true }) {
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [eventos, setEventos] = useState([]);
  const eventosBasicos = useMemo(() => criarEventosBasicos(registro), [registro]);
  const eventosIncorporados = useMemo(
    () => Array.isArray(registro?.historico) ? registro.historico : [],
    [registro]
  );
  const clienteLogado = String(localStorage.getItem("tipoUsuario") || "").toLowerCase() === "cliente";

  if (clienteLogado) return null;

  async function resolverResponsaveis(lista) {
    if (!resolverEmailPorUid) return lista;

    return Promise.all(lista.map(async (item) => {
      const usuarioObjeto = item.usuario && typeof item.usuario === "object" ? item.usuario : null;
      const emailRegistrado = item.usuarioEmail || item.userEmail || item.responsavelEmail || item.email || usuarioObjeto?.email;
      if (emailRegistrado) {
        if (!item.usuarioEmail) return { ...item, usuarioEmail: emailRegistrado };
        return item;
      }
      const uid = item.usuarioUid || item.userId || item.criadoPorUid || item.atualizadoPorUid || usuarioObjeto?.uid || item.usuario;
      if (!uid || String(uid).includes("@") || String(uid).includes(" ")) return item;
      const uidTexto = String(uid);
      const usuarioAtual = auth.currentUser;

      if (usuarioAtual?.uid === uidTexto && usuarioAtual.email) {
        return { ...item, usuarioEmail: usuarioAtual.email };
      }

      try {
        const usuarioSnap = await getDoc(doc(db, "users", uidTexto));
        const dadosUsuario = usuarioSnap.exists() ? usuarioSnap.data() : null;
        const email = dadosUsuario?.email || dadosUsuario?.usuarioEmail || dadosUsuario?.userEmail || "";
        return email
          ? { ...item, usuarioEmail: email }
          : { ...item, usuario: "E-mail não localizado" };
      } catch {
        return { ...item, usuario: "E-mail não localizado" };
      }
    }));
  }

  async function abrir() {
    setAberto(true);
    if (!documentoId) {
      setEventos(eventosBasicos);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    try {
      const snapshot = await getDocs(collection(db, colecao, documentoId, "historico"));
      const gravados = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => {
          const dataA = converterData(a.data || a.dataHora || a.criadoEm || a.createdAt)?.getTime() || 0;
          const dataB = converterData(b.data || b.dataHora || b.criadoEm || b.createdAt)?.getTime() || 0;
          return dataB - dataA;
        });
      const origem = gravados.length
        ? gravados
        : eventosIncorporados.length
          ? [...eventosIncorporados].sort((a, b) => {
              const dataA = converterData(a.data || a.dataHora || a.criadoEm || a.createdAt)?.getTime() || 0;
              const dataB = converterData(b.data || b.dataHora || b.criadoEm || b.createdAt)?.getTime() || 0;
              return dataB - dataA;
            })
          : eventosBasicos;
      setEventos(await resolverResponsaveis(origem));
    } catch (erro) {
      console.info(`Histórico detalhado indisponível para ${colecao}/${documentoId}.`, erro?.code || erro);
      setEventos(await resolverResponsaveis(eventosIncorporados.length ? [...eventosIncorporados].reverse() : eventosBasicos));
    } finally {
      setCarregando(false);
    }
  }

  function fechar(evento) {
    evento?.stopPropagation?.();
    setAberto(false);
  }

  const acionador = iconeApenas ? (
    <Tooltip title={documentoId ? "Consultar histórico" : "Histórico disponível após salvar"}>
      <IconButton size="small" color="primary" onClick={(evento) => { evento.stopPropagation(); abrir(); }}>
        <HistoryIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={abrir}>
      Histórico
    </Button>
  );

  return (
    <>
      {acionador}
      <Dialog
        open={aberto}
        onClose={fechar}
        onClick={(evento) => evento.stopPropagation()}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Histórico de alterações</DialogTitle>
        <DialogContent dividers>
          {carregando && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>}
          {!carregando && eventos.length === 0 && (
            <Typography color="text.secondary">
              Nenhuma alteração foi registrada para este cadastro.
            </Typography>
          )}
          {!carregando && eventos.map((item) => (
            <Box key={item.id} sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Typography fontWeight={700}>{normalizarTitulo(item)}</Typography>
              <Typography fontSize={13}>{item.descricao || "Alteração registrada no sistema."}</Typography>
              {item.campo && (
                <Typography fontSize={13}>
                  Campo: {item.campo} — de “{String(item.valorAnterior ?? "não informado")}” para “{String(item.valorNovo ?? "não informado")}”.
                </Typography>
              )}
              <Typography fontSize={12} color="text.secondary">
                Data e hora: {formatarDataHora(item.data || item.dataHora || item.criadoEm || item.createdAt)}
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                Responsável: {formatarResponsavel(item.usuarioEmail, item.userEmail, item.responsavelEmail, item.email, item.usuario, item.usuarioNome, item.userName)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={fechar}>Fechar</Button></DialogActions>
      </Dialog>
    </>
  );
}
